/**
 * Accent-, case- and punctuation-insensitive normalisation used for peak search
 * on both the client (catalog) and the server (the name_norm column).
 * "Pica d'Estats" → "pica d estats", "Tuca de Mulleres" → "tuca de mulleres".
 */
export function normalizeName(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/ß/g, "ss")
    .replace(/[æ]/g, "ae")
    .replace(/[œ]/g, "oe")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** Generic words that should not drive name-similarity dedupe. */
const STOP = new Set([
  "pico", "pic", "punta", "tuca", "tuc", "tossal", "tuco", "puig", "mont", "mount", "cima", "cim",
  "de", "del", "la", "las", "los", "el", "le", "les", "d", "l", "des", "du", "dera", "era", "deth", "eth",
  "sierra", "serra", "tuc", "picu", "pena", "peña", "tozal",
]);

function tokens(s: string): string[] {
  return normalizeName(s)
    .split(" ")
    .filter((t) => t && !STOP.has(t));
}

/**
 * Similarity in [0,1] between two peak names: token Jaccard with a bonus when one
 * name's meaningful tokens are a subset of the other's ("Aneto" vs "Pico de Aneto").
 */
export function nameSimilarity(a: string, b: string): number {
  const ta = new Set(tokens(a));
  const tb = new Set(tokens(b));
  if (ta.size === 0 || tb.size === 0) return normalizeName(a) === normalizeName(b) ? 1 : 0;
  let inter = 0;
  for (const t of ta) if (tb.has(t)) inter++;
  if (inter === Math.min(ta.size, tb.size)) return Math.max(0.85, inter / Math.max(ta.size, tb.size));
  return inter / (ta.size + tb.size - inter);
}

export function csvEscape(v: unknown): string {
  if (v === null || v === undefined) return "";
  const s = typeof v === "string" ? v : typeof v === "object" ? JSON.stringify(v) : String(v);
  return /[",\n\r;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(rows: Record<string, unknown>[], columns?: string[]): string {
  const cols = columns ?? (rows[0] ? Object.keys(rows[0]) : []);
  const lines = [cols.join(",")];
  for (const r of rows) lines.push(cols.map((c) => csvEscape(r[c])).join(","));
  return lines.join("\r\n") + "\r\n";
}

/** Minimal RFC 4180 CSV parser (handles quotes, CRLF, `,` or `;` delimiter). */
export function parseCsv(input: string): string[][] {
  const src = input.replace(/^\uFEFF/, "");
  const firstLine = src.split(/\r?\n/, 1)[0] ?? "";
  const delim = (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? ";" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += c;
    } else if (c === '"') quoted = true;
    else if (c === delim) {
      row.push(field);
      field = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && src[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else field += c;
  }
  if (field !== "" || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((f) => f.trim() !== ""));
}

// ── Text people write for others to read (name, bio, comments) ────────────────────────────────────

/**
 * What others read of a user's text: control and zero-width characters removed, line endings
 * normalised, at most `maxBreaks` line breaks (the rest become spaces), runs of spaces collapsed.
 */
export function cleanUserText(s: string, maxBreaks = 2): string {
  let breaks = 0;
  return s
    .replace(/\r\n?/g, "\n")
    .replace(INVISIBLE, "")
    .replace(/\n/g, () => (++breaks <= maxBreaks ? "\n" : " "))
    .replace(/[ \t]+/g, " ")
    .replace(/ ?\n ?/g, "\n")
    .trim();
}

/** Control characters (but the line break) and the invisible ones: zero-width, bidi overrides, BOM. */
// eslint-disable-next-line no-control-regex -- matching control characters is the point
const INVISIBLE = /[\u0000-\u0009\u000b-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2060-\u2064\ufeff]/g;

/** Lower case, no accents, digits read as the letters they stand in for ("adm1n" → "admin"). */
function fold(s: string): string {
  return normalizeName(s.replace(/[0134578]/g, (d) => "oieastb"["0134578".indexOf(d)]!));
}

/**
 * Words never allowed in a handle, a name or a bio (slurs and insults in Spanish and English), matched
 * whole after folding. A short list on purpose: reports catch the rest.
 */
const BLOCKED_WORDS = new Set([
  "maricon", "maricona", "marica", "bollera", "sudaca", "negrata", "panchito", "moromierda", "subnormal", "retrasado",
  "mongolo", "puta", "puto", "zorra", "hijoputa", "hijodeputa", "nazi", "nigger", "nigga", "faggot", "fag", "retard",
  "tranny", "whore", "slut", "cunt",
]);

/** False when a text holds a blocked word ("Ese texto no está permitido"). */
export function allowedText(s: string): boolean {
  return !fold(s)
    .split(" ")
    .some((w) => BLOCKED_WORDS.has(w));
}

/** Handles no one can take, even spelt with dots, underscores or digits for letters: the app's and the team's names. */
const RESERVED_HANDLES = new Set([
  "admin", "administrador", "administrator", "root", "staff", "soporte", "support", "ayuda", "help", "moderacion",
  "moderador", "moderator", "oficial", "official", "topsummits", "peakstamps", "sistema", "system",
]);

/** The app's route and section words, taken only as they are ("amigo_5" is fine, "amigos" is not). */
const ROUTE_WORDS = new Set([
  "api", "app", "www", "amigos", "personas", "ajustes", "perfil", "pasaporte", "mapa", "cimas", "cumbres", "listas",
  "ascensiones", "estadisticas", "clasificacion", "actividad", "avisos", "privacidad", "terminos", "nueva", "editar",
  "buscar", "yo", "me", "todos", "nadie",
]);

/** A handle someone may claim: not reserved nor offensive. */
export function allowedHandle(handle: string): boolean {
  const folded = fold(handle.replace(/[._]/g, ""));
  return !ROUTE_WORDS.has(handle.toLowerCase()) && !RESERVED_HANDLES.has(folded) && allowedText(handle.replace(/[._]/g, " ")) && !BLOCKED_WORDS.has(folded);
}
